import React from "react";
import "./Subscriptions.css";
import styled from "styled-components";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { useTranslation } from 'react-i18next';
import CustomLink from "../../../../Components/CustomLink/CustomLink";

const Subscriptions = () => {
  const { t } = useTranslation();
  
  const scrollToContact = () => {
    const contactSection = document.getElementById("contact");
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Container id="pricing">
      <HeaderText className="archivo-regular">
        {t('Pricing.sampleTransparentFlexible')}
      </HeaderText>
      <SubHeaderText className="archivo-bold">
        {t('Pricing.ourPricingPlans')}
      </SubHeaderText>
      
      <CardGrid>
        {/* Free Plan */}
        <PricingCard>
          <StyledPricingHeader>
            <SubscriptionLeftHeader>
              <SubscriptionTitle className="poppins-bold">
                {t('Pricing.freePlanTitle')}
              </SubscriptionTitle>
              <SubscriptionPeriod>
                {t('Pricing.freePlanPeriod')}
              </SubscriptionPeriod>
            </SubscriptionLeftHeader>
            <Price>{t('Pricing.freePlanPrice')}</Price>
          </StyledPricingHeader>
          
          <StyledPricingBody>
            <SubscriptionDescription>
              {t('Pricing.freePlanDescription')}
            </SubscriptionDescription>
            
            <SubscriptionList>
              {[1, 2, 3, 4].map((item) => (
                <SubscriptionListItem key={item}>
                  <KeyboardDoubleArrowRightIcon sx={{ color: "#664de4" }} />
                  <span>{t(`Pricing.freePlanFeature${item}`)}</span>
                </SubscriptionListItem>
              ))}
            </SubscriptionList>
            
            <CustomLink  to={'#'}  className="rounded-button filled-button action-button">
              {t('Pricing.getStarted')}
            </CustomLink>
          </StyledPricingBody>
        </PricingCard>

        {/* Pro Plan */}
        <PricingCard popular>
          <PopularTag>{t('Pricing.popular')}</PopularTag>
          <StyledPricingHeader>
            <SubscriptionLeftHeader>
              <SubscriptionTitle className="poppins-bold">
                {t('Pricing.proPlanTitle')}
              </SubscriptionTitle>
              <SubscriptionPeriod>
                {t('Pricing.proPlanPeriod')}
              </SubscriptionPeriod>
            </SubscriptionLeftHeader>
            <Price>
              <strong>{t('Pricing.proPlanPrice')}</strong>
              <PerReservation>{t('Pricing.perReservation')}</PerReservation>
            </Price>
          </StyledPricingHeader>
          
          <StyledPricingBody>
            <SubscriptionDescription>
              {t('Pricing.proPlanDescription')}
            </SubscriptionDescription>
            
            <SubscriptionList>
              {[1, 2, 3, 4].map((item) => (
                <SubscriptionListItem key={item}>
                  <KeyboardDoubleArrowRightIcon sx={{ color: "#664de4" }} />
                  <span>{t(`Pricing.proPlanFeature${item}`)}</span>
                </SubscriptionListItem>
              ))}
            </SubscriptionList>
            
            <PricingNote>
              {t('Pricing.proPlanNote')}
            </PricingNote>
            
            <CustomLink  to={'#'}  className="rounded-button filled-button action-button">
              {t('Pricing.getStarted')}
            </CustomLink>
          </StyledPricingBody>
        </PricingCard>

        {/* Business Plan */}
        <PricingCard>
          <StyledPricingHeader>
            <SubscriptionLeftHeader>
              <SubscriptionTitle className="poppins-bold">
                {t('Pricing.businessPlanTitle')}
              </SubscriptionTitle>
              <SubscriptionPeriod>
                {t('Pricing.businessPlanPeriod')}
              </SubscriptionPeriod>
            </SubscriptionLeftHeader>
            <Price>{t('Pricing.businessPlanPrice')}</Price>
          </StyledPricingHeader>
          
          <StyledPricingBody>
            <SubscriptionDescription>
              {t('Pricing.businessPlanDescription')}
            </SubscriptionDescription>
            
            <SubscriptionList>
              {[1, 2, 3].map((item) => (
                <SubscriptionListItem key={item}>
                  <KeyboardDoubleArrowRightIcon sx={{ color: "#664de4" }} />
                  <span>{t(`Pricing.businessPlanFeature${item}`)}</span>
                </SubscriptionListItem>
              ))}
            </SubscriptionList>
            
            <CustomLink   to={'#'} 
              className="rounded-button filled-button action-button"
              onClick={scrollToContact}
            >
              {t('Pricing.contactUs')}
            </CustomLink>
          </StyledPricingBody>
        </PricingCard>
      </CardGrid>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  padding: 80px 20px;
  text-align: center;
  max-width: 1200px;
  margin: 0 auto;
`;

const HeaderText = styled.h2`
  font-size: 1.2rem;
  color: #664de4;
  letter-spacing: 2px;
  margin-bottom: 10px;
  text-transform: uppercase;
`;

const SubHeaderText = styled.h3`
  font-size: 2.5rem;
  margin-bottom: 50px;
  color: #333;
`;

const CardGrid = styled.div`
  display: flex;
  gap: 100px;
  justify-content: center;
  flex-wrap: wrap;
`;

const PricingCard = styled.div`
  background: white;
  border: ${({ popular }) => popular ? "2px solid #664de4" : "1px solid #ddd"};
  border-radius: 10px;
  width: 320px;
  position: relative;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
  transition: transform 0.3s;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0,0,0,0.1);
  }
`;

const PopularTag = styled.div`
  background-color: #664de4;
  color: white;
  font-size: 0.8rem;
  font-weight: bold;
  padding: 5px 15px;
  border-radius: 20px;
  position: absolute;
  top: -12px;
  right: 20px;
  z-index: 1;
`;

const StyledPricingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px;
  border-bottom: 1px solid #eee;
`;

const SubscriptionLeftHeader = styled.div`
  text-align: left;
`;

const SubscriptionTitle = styled.h4`
  font-size: 1.3rem;
  margin: 0;
  color: #333;
`;

const SubscriptionPeriod = styled.p`
  margin: 5px 0 0;
  color: #666;
  font-size: 0.9rem;
`;

const Price = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: #664de4;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const PerReservation = styled.span`
  font-size: 0.8rem;
  font-weight: normal;
  color: #666;
  margin-top: 5px;
`;

const StyledPricingBody = styled.div`
  padding: 25px;
  text-align: left;
`;

const SubscriptionDescription = styled.p`
  color: #666;
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 20px;
`;

const SubscriptionList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 25px;
`;

const SubscriptionListItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  font-size: 0.95rem;
  color: #444;
  
  svg {
    margin-right: 10px;
    flex-shrink: 0;
    margin-top: 3px;
  }
`;

const PricingNote = styled.p`
  background: #f8f9ff;
  padding: 12px;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #664de4;
  margin: 20px 0;
`;

export default Subscriptions;

// const StyledPricingHeader = styled.div`
//   display: flex;
//   justify-content: space-between;
//   width: 100%;
//   border-bottom: 1px solid rgba(204, 204, 204, 0.5);
// `;

// const StyledPricingCard = styled.div`
//   padding: 30px;
// `;

// const SubscriptionTitle = styled.p`
//   font-size: 1.3rem;
//   font-weight: bold;
//   margin-bottom: 0;
// `;

// const SubscriptionPeriod = styled.p`
//   font-size: 1rem;
//   color: #777;
// `;

// const SubscriptionLeftHeader = styled.div`
//   display: flex;
//   flex-direction: column;
//   align-items: flex-start;
// `;

// const StyledPricingBody = styled.div`
//   text-align: left;
//   padding-top: 40px;
//   display: flex;
//   flex-direction: column;
//   justify-content: space-between;
//   position: relative;
//   flex: 1;
// `;

// const SubscriptionDescription = styled.p`
//   font-size: 0.9rem;
// `;

// const SubscriptionList = styled.ul`
//   margin-top: 30px;
//   list-style: none;
//   padding-left: 0;
// `;

// const SubscriptionListItem = styled.li`
//   span {
//     margin-left: 10px;
//     color: #555;
//   }
// `;

// const StyledButton = styled.button`
//   background-color: ${(p) => p.theme.colors.primary};
//   border-radius: 10px;
//   width: 100%;
//   height: 54px;
//   text-transform: uppercase;
//   font-weight: bold;
//   transition: all 0.3s ease-in-out;
//   position: relative;
//   bottom: 0;
//   &:hover {
//     opacity: 0.9;
//   }
// `;

// const StyledH3 = styled.h3`
//   font-size: 2.8rem;
//   @media (max-width:900px) {
//     text-align: center;
//   }
//   @media (max-width:1024px) {
//     font-size: 2rem;
//   }
// `;

// export default Subscriptions;

      // {/* Pricing Section */}
      // <section id="pricing" className="py-20 bg-white/80 backdrop-blur-sm">
      //   <div className="max-w-7xl mx-auto px-4">
      //     <h2 className="text-3xl font-bold text-center mb-16">Free plan</h2>
      //     <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
      //       {/* Unit Plan */}
      //       <div className="relative bg-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-erwii-purple/20 group flex flex-col">
      //         <div className="absolute -top-3 -right-3 w-24 h-24 bg-gradient-to-br from-erwii-purple/20 to-erwii-blue/20 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500"></div>
      //         <div className="relative flex-1 flex flex-col">
      //           <h3 className="text-2xl font-semibold h-[72px] flex items-center bg-gradient-to-r from-erwii-purple to-erwii-blue bg-clip-text text-transparent">
      //             title1
      //           </h3>
      //           <div className="h-[100px] flex flex-col justify-center">
      //             <p className="text-4xl font-bold">
      //               price 1
      //               <span className="text-lg text-gray-500 ml-1">period</span>
      //             </p>
      //           </div>
      //           <div className="h-px w-full bg-gradient-to-r from-erwii-purple/20 to-erwii-blue/20 my-6"></div>
      //           <ul className="space-y-4 flex-1">
      //             {/* {t.pricing.unit.features.map((feature, index) => (
      //               <li key={`unit-feature-${index}`} className="flex items-start gap-3">
      //                 <Star className="text-erwii-yellow flex-shrink-0 mt-1" size={18} />
      //                 <span className="text-gray-600">{feature}</span>
      //               </li>
      //             ))} */}
      //           </ul>
      //         </div>
      //       </div>

      //       {/* Monthly Plan */}
      //       <div className="relative bg-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-erwii-purple transform hover:-translate-y-1 flex flex-col">
      //         <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-erwii-purple text-white px-4 py-1 rounded-full text-sm font-medium">
      //           Popular
      //         </div>
      //         <div className="absolute -top-3 -right-3 w-32 h-32 bg-gradient-to-br from-erwii-purple/30 to-erwii-blue/30 rounded-full blur-2xl"></div>
      //         <div className="relative flex-1 flex flex-col">
      //           <h3 className="text-2xl font-semibold h-[72px] flex items-center bg-gradient-to-r from-erwii-purple to-erwii-blue bg-clip-text text-transparent">
      //            title 2
      //           </h3>
      //           <div className="h-[100px] flex flex-col justify-center">
      //             <p className="text-4xl font-bold">
      //               price 2
      //               <span className="text-lg text-gray-500 ml-1">periode</span>
      //             </p>
      //           </div>
      //           <div className="h-px w-full bg-gradient-to-r from-erwii-purple to-erwii-blue/50 my-6"></div>
      //           <ul className="space-y-4 flex-1">
      //             {/* {t.pricing.monthly.features.map((feature, index) => (
      //               <li key={`monthly-feature-${index}`} className="flex items-start gap-3">
      //                 <Star className="text-erwii-yellow flex-shrink-0 mt-1" size={18} />
      //                 <span className="text-gray-600">{feature}</span>
      //               </li>
      //             ))} */}
      //           </ul>
      //         </div>
      //       </div>

      //       {/* Yearly Plan */}
      //       <div className="relative bg-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-erwii-purple/20 group flex flex-col">
      //         <div className="absolute -top-3 -right-3 w-24 h-24 bg-gradient-to-br from-erwii-purple/20 to-erwii-blue/20 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500"></div>
      //         <div className="relative flex-1 flex flex-col">
      //           <h3 className="text-2xl font-semibold h-[72px] flex items-center bg-gradient-to-r from-erwii-purple to-erwii-blue bg-clip-text text-transparent">
      //             title 3
      //           </h3>
      //           <div className="h-[100px] flex flex-col justify-center">
      //             <p className="text-4xl font-bold">
      //               price 3
      //               <span className="text-lg text-gray-500 ml-1">period</span>
      //             </p>
      //           </div>
      //           <div className="h-px w-full bg-gradient-to-r from-erwii-purple/20 to-erwii-blue/20 my-6"></div>
      //           <ul className="space-y-4 flex-1">
      //             {/* {t.pricing.yearly.features.map((feature, index) => (
      //               <li key={`yearly-feature-${index}`} className="flex items-start gap-3">
      //                 <Star className="text-erwii-yellow flex-shrink-0 mt-1" size={18} />
      //                 <span className="text-gray-600">{feature}</span>
      //               </li>
      //             ))} */}
      //           </ul>
      //         </div>
      //       </div>
      //     </div>
      //   </div>
      // </section>


// import React from 'react';
// import { useNavigate } from 'react-router-dom';
// import './Subscriptions.css';
// import { useTranslation } from 'react-i18next';

// const Subscriptions = () => {
//   const navigate = useNavigate();
//   const { t } = useTranslation();

//   const handleFreeClick = () => {
//     navigate('/register');
//   };

//   const handleProClick = () => {
//     navigate('/professionalcreation');
//   };

//   return (
//     <div className="subscription-packages">
//       <div className="subscription-package">
//         <span className='header'>{t('HomePage.Subscription_allUsers')}</span>
//         <span className='headerSub'>{t('HomePage.Subscription_forLife')}</span>
//         <span className='price'>{t('HomePage.Subscription_free')}</span>
//         <span className='priceSub'></span>
//         <button className='pricingbutton' onClick={handleFreeClick}>{t('HomePage.Subscription_getStarted')}</button>
//         <span className='pricingperiod'>{t('HomePage.Subscription_noSubscription')}</span>
//         <ul className='pricingoptions'>
//           <li className='table-feature-inner'>
//             <svg className="svg_tick" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
//               <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
//             </svg>
//             <span>{t('HomePage.Subscription_syncPlatforms')}</span>
//           </li>
//           <li className='table-feature-inner'>
//             <svg className="svg_tick" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
//               <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
//             </svg>
//             <span>{t('HomePage.Subscription_searchProfessionals')}</span>
//           </li>
//           <li className='table-feature-inner'>
//             <svg className="svg_tick" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
//               <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
//             </svg>
//             <span>{t('HomePage.Subscription_cancelEvents')}</span>
//           </li>
//           <li className='table-feature-inner'>
//             <svg className="svg_tick" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
//               <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
//             </svg>
//             <span>{t('HomePage.Subscription_receiveNotifications')}</span>
//           </li>
//         </ul>
//       </div>
//       <div className="subscription-package">
//         <span className='header'>{t('HomePage.Subscription_pro')}</span>
//         <span className='headerSub'>{t('HomePage.Subscription_getStarted')}</span>
//         <span className='price'></span>
//         <span className='priceSub'>/For 3 months</span>
//         <button className='pricingbutton' onClick={handleProClick}>{t('HomePage.Subscription_subscribe')}</button>
//         <span className='pricingperiod'>{t('HomePage.Subscription_manageEvents')}</span>
//         <ul className='pricingoptions'>
//           <li className='table-feature-inner'>
//             <svg className="svg_tick" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
//               <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
//             </svg>
//             <span>{t('HomePage.Subscription_manageClientsTimes')}</span>
//           </li>
//           <li className='table-feature-inner'>
//             <svg className="svg_tick" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
//               <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
//             </svg>
//             <span>{t('HomePage.Subscription_automationRescheduling')}</span>
//           </li>
//           <li className='table-feature-inner'>
//             <svg className="svg_tick" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
//               <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
//             </svg>
//             <span>{t('HomePage.Subscription_manageTeamsSchedules')}</span>
//           </li>
//           <li className='table-feature-inner'>
//             <svg className="svg_tick" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
//               <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
//             </svg>
//             <span>{t('HomePage.Subscription_manageClientsTimes')}</span>
//           </li>
//         </ul>
//       </div>
//       <div className="subscription-package">
//         <span className='header'>{t('HomePage.Subscription_pro')}</span>
//         <span className='headerSub'>{t('HomePage.Subscription_monthlyYearly')}</span>
//         <span className='price'>50$ </span>
//         <span className='priceSub'>/{t('HomePage.Subscription_monthYear')}</span>
//         <button className='pricingbutton' onClick={handleProClick}>{t('HomePage.Subscription_subscribe')}</button>
//         <span className='pricingperiod'>{t('HomePage.Subscription_manageEvents')}</span>
//         <ul className='pricingoptions'>
//           <li className='table-feature-inner'>
//             <svg className="svg_tick" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
//               <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
//             </svg>
//             <span>{t('HomePage.Subscription_manageClientsTimes')} </span>
//           </li>
//           <li className='table-feature-inner'>
//             <svg className="svg_tick" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
//               <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
//             </svg>
//             <span>{t('HomePage.Subscription_automationRescheduling')}</span>
//           </li>
//           <li className='table-feature-inner'>
//             <svg className="svg_tick" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
//               <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
//             </svg>
//             <span>{t('HomePage.Subscription_manageTeamsSchedules')}</span>
//           </li>
//           <li className='table-feature-inner'>
//             <svg className="svg_tick" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
//               <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
//             </svg>
//             <span>{t('HomePage.Subscription_manageClientsTimes')}</span>
//           </li>
//         </ul>
//       </div>
//       <div className="subscription-package">
//        <span className='header'>{t('HomePage.Subscription_business')}</span>
//         <span className='headerSub'>{t('HomePage.Subscription_monthlyYearly')}</span>
//         <button style={{marginTop:'110px'}} className='pricingbutton'>{t('HomePage.Subscription_contactSales')}</button>
//         <span className='price'></span>
//         <span className='priceSub'></span>
//         <span className='pricingperiod'>{t('HomePage.Subscription_exclusivesFunctionalities')}</span>
        
//         <ul  className='pricingoptions'>
//           <li className='table-feature-inner'>  <svg
//             className="svg_tick"
//             viewBox="0 0 12 12"
//             xmlns="http://www.w3.org/2000/svg"
//           >
//             <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
//           </svg>
//             <span>{t('HomePage.Subscription_integrationSystems')}</span></li>
//           <li className='table-feature-inner'><svg
//             className="svg_tick"
//             viewBox="0 0 12 12"
//             xmlns="http://www.w3.org/2000/svg"
//           >
//             <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
//           </svg>
//             <span>{t('HomePage.Subscription_manageExternalContacts')}</span></li>
//           <li className='table-feature-inner'><svg
//             className="svg_tick"
//             viewBox="0 0 12 12"
//             xmlns="http://www.w3.org/2000/svg"
//           >
//             <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
//           </svg>
//             <span>{t('HomePage.Subscription_automationProcesses')}</span></li>
//         </ul>
//       </div>
//     </div>
//   );
// };

// export default Subscriptions;

